// =============================
// Imports
// =============================

// External Dependencies
import { Icons } from '@mewo/components';
import playlistplaceholder from '@mewo/components/assets/images/placeholders/playlist_500x500.png?webp';
import arrayMutators from 'final-form-arrays';
import _get from 'lodash/get';
import Router from 'next/router';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { compose } from 'redux';

// Config
import { withTranslation } from '../../../../config/i18n';
import entityConfig from '../../meta/briefs/config';

// Actions
import { panelActions, triggerAutosaveCheck } from '../../../../store/actions/SidePanelActions';

// Constants
import * as spts from '../../../../store/constants/SidePanelTypes';

// Components
import InputWithLanguage from '../../../containers/inputWithLanguage';
import BaseSidePanel from '../../../faac/panels/base';
import WithMetaSearchRoute from '../../../faac/withMetaSearchRoute';
import Autosave from '../../../other/autosave';
import MetaDownloadDropdown from '../../../other/metaDownloadDropdown';
import DatePickerInput from '../../../presentationals/inputs/datePickerInput';
import Input from '../../../presentationals/inputs/input';
import SelectInput from '../../../presentationals/inputs/selectInput';
import TextAreaInput from '../../../presentationals/inputs/textAreaInput';
import MewoInput from '../../../presentationals/mewoInput';
import Uploader from '../../../presentationals/uploader';
import Autotag from '../autotag';
import CopyLink from '../copyLink';
import DocumentStatus from '../documentStatus';
import DocumentTracks from '../documentTracks';
import MetaCustomFields from '../metaCustomFields';
import MetaRightsInformations from '../metaRightsInformations';

import validator from '../../../../validators/meta/playlist';

// Helpers
import { formatDateString } from '../../../../helpers/dates';
import { getDocumentRights, getGenericDocCoverUrl } from '../../../../helpers/meta-common';
import { getSiteOrigin } from '../../../../helpers/misc';
import * as pth from '../../../../helpers/proptypes';

// Styles
import { InputDouble, InputWrapper } from '../../global.styles';
import {
  DocumentBody,
  DocumentCreatedAt,
  DocumentName,
  DocumentType,
  DownloadActionIcon,
  DownloadActionWrapper,
  FileHelper,
  GenericCover,
  GenericOwnership,
  GenericOwnershipCount,
  GenericSearchIcon,
  GenericSearchLink,
  LeftColumn,
  LineInnerLoader,
  LineLoader,
  LineLoaderWrapper,
  MiscHelper,
  RelationJobTooltip,
  RelationsWrapper,
  RelationTitleWrapper,
  RightColumn,
  SectionBox,
  SectionNotice,
  SectionTitle,
  TabElement,
  TabLink,
  TabLinksWrapper,
} from '../common.styles';

// =============================
// Component
// =============================

const entity = 'brief';

class MetaBrief extends Component {
  static propTypes = {
    tenantId: PropTypes.string.isRequired,
    panel: PropTypes.shape({
      data: pth.brief,
      additionalData: PropTypes.shape({
        tracks: PropTypes.shape({
          data: PropTypes.arrayOf(pth.smallTrack),
        }),
        autotagging: PropTypes.shape({
          data: PropTypes.shape({
            nb_tracks: PropTypes.number,
            nb_tracks_not_autotagged: PropTypes.number,
          }),
        }),
      }),
      isLoading: PropTypes.bool.isRequired,
      'isLoading-tracks': PropTypes.bool,
      isModifying: PropTypes.bool.isRequired,
      'isModifying-ingest': PropTypes.bool,
      'isModifying-tracks': PropTypes.bool,
      isAutotagging: PropTypes.bool.isRequired,
      isUploading: PropTypes.bool.isRequired,
      isDeletingUpload: PropTypes.bool.isRequired,
      isDeleting: PropTypes.bool.isRequired,
      uploadProgress: PropTypes.number.isRequired,
    }).isRequired,
    localPreferences: pth.localPreferences.isRequired,
    relationsNotifications: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      progress: PropTypes.number,
      isAudioIngestion: PropTypes.bool,
    })),
    customFields: pth.customFieldsConfig.isRequired,
    providers: PropTypes.arrayOf(pth.provider).isRequired,
    triggerPanelAutosaveCheck: PropTypes.func.isRequired,
    i18n: PropTypes.shape({
      language: PropTypes.string,
    }).isRequired,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    relationsNotifications: [],
  };

  componentDidUpdate(prevProps) {
    const { relationsNotifications: prevRelationsNotifications } = prevProps;
    const { relationsNotifications, panel: { data } } = this.props;

    if (data) {
      const prevAudioRelationsNotification = prevRelationsNotifications
        .find(notification => notification.id === data.id && notification.isAudioIngestion);

      const audioRelationsNotification = relationsNotifications
        .find(notification => notification.id === data.id && notification.isAudioIngestion);

      if (
        typeof this.refreshPanel === 'function'
        && !!prevAudioRelationsNotification
        && !audioRelationsNotification
      ) {
        this.refreshPanel(true);
      }
    }
  }

  pickFormData = (data, documentRights, init = false) => {
    if (!documentRights.canWrite && !init) {
      return {};
    }

    const keys = {
      title: _get(data, 'title', ''),
      descriptions: _get(data, 'descriptions', [])
        .map(({ locale, value }) => ({ locale, value })),
    };

    if (documentRights.isOwner || init) {
      keys.budget = _get(data, 'budget', null);
      keys.currency = _get(data, 'currency', '');
      keys.deadline = _get(data, 'deadline', null);
    }

    if (documentRights.canAccessNotes) {
      keys.notes = _get(data, 'notes', '');
    }

    if (documentRights.isOwner) {
      keys.public = _get(data, 'public', false);
      keys.agents = _get(data, 'agents', []);

      keys.custom = _get(data, 'custom', {});
    }

    return keys;
  };

  renderPanelAnchors = (data) => {
    const { customFields, providers, t } = this.props;

    const documentRights = getDocumentRights(
      data.owned_by_tenant,
      data.tenant.id,
      providers,
    );

    const hasCustomFields = !!customFields.fields
      .filter(customField => customField.collections.includes(entity))
      .length;

    // Owner view:
    // - Tracks
    // - Info
    // - Custom fields (if exists)
    // - Agents
    // - Uploads

    // Agents brief view:
    // - Info
    // - Tracks
    return (
      <TabLinksWrapper>
        {documentRights.isOwner && (
          <TabLink name="tracks">
            {t(`pages:meta.${entity}s.panel.tracks_anchor`)}
          </TabLink>
        )}
        <TabLink name="general_information">
          {t(`pages:meta.${entity}s.panel.general_information_anchor`)}
        </TabLink>
        {!documentRights.isOwner && (
          <TabLink name="tracks">
            {t(`pages:meta.${entity}s.panel.tracks_anchor`)}
          </TabLink>
        )}
        {documentRights.isOwner && (
          <Fragment>
            {hasCustomFields && (
              <TabLink name="custom_fields_information">
                {t(`pages:meta.${entity}s.panel.custom_fields_information_anchor`)}
              </TabLink>
            )}
            <TabLink name="agents_information">
              {t(`pages:meta.${entity}s.panel.agents_information_anchor`)}
            </TabLink>
            <TabLink name="uploads_information">
              {t(`pages:meta.${entity}s.panel.uploads_information_anchor`)}
            </TabLink>
          </Fragment>
        )}
      </TabLinksWrapper>
    );
  };

  render() {
    const {
      tenantId,
      panel,
      localPreferences,
      relationsNotifications,
      customFields,
      providers,
      i18n: { language },
      t,
    } = this.props;

    const {
      data,
      additionalData,
      isModifying,
      isAutotagging,
      isUploading,
      isDeletingUpload,
      isDeleting,
      uploadProgress,
    } = panel;

    const hasCustomFields = !!customFields.fields
      .filter(customField => customField.collections.includes(entity))
      .length;

    const shareLink = _get(data, 'id')
      ? `${getSiteOrigin()}/meta/${entity}s?panel=${data.id}`
      : '';

    let inputDateFormat = 'DD/MM/YYYY';
    if (localPreferences.dateFormat === 'MMDDYYYY') inputDateFormat = 'MM/DD/YYYY';

    return (
      <BaseSidePanel
        {...this.props}
        get={panelActions[spts.META_BRIEF_PANEL].get}
        getAdditionalOptions={{
          fnc: panelActions[spts.META_BRIEF_PANEL].getAdditional,
          list: [
            ['tracks', 'tracks'],
            [
              `/meta/autotagging/${entity}/${panel.id}/data`,
              'autotagging',
              '',
              panelData => panelData?.owned_by_tenant,
            ],
          ],
        }}
        requestGridRefresh={panelActions[spts.META_BRIEF_PANEL].requestGridRefresh}
        modify={panelActions[spts.META_BRIEF_PANEL].modify}
        modifyAdditional={panelActions[spts.META_BRIEF_PANEL].modifyAdditional}
        autotagOptions={{
          fnc: panelActions[spts.META_BRIEF_PANEL].autotag,
          getAdditionalList: [
            [
              `/meta/autotagging/${entity}/${panel.id}/data`,
              'autotagging',
            ],
          ],
        }}
        uploadFile={panelActions[spts.META_BRIEF_PANEL].uploadFile}
        deleteFile={panelActions[spts.META_BRIEF_PANEL].deleteFile}
        del={panelActions[spts.META_BRIEF_PANEL].delete}
        lang={{
          autotagTitle: t(`pages:meta.${entity}s.autotag_documents`, { name: data?.title }),
          autotagDesc: t(`pages:meta.${entity}s.autotag_documents_notice`),
          deleteConfirmTitle: t(`pages:meta.${entity}s.delete_documents`),
          deleteConfirmDesc: t(`pages:meta.${entity}s.delete_documents_notice`),
        }}
      >
        {({
          refreshPanel,
          modifyDocument,
          modifyAdditional,
          autotagDocument,
          uploadDocumentFile,
          deleteDocumentFile,
          deleteDocument,
        }) => {
          if (!data) return null;

          this.refreshPanel = refreshPanel;

          const relationsNotification = relationsNotifications
            .find(notification => notification.id === data.id);

          const documentRights = getDocumentRights(
            data.owned_by_tenant,
            data.tenant.id,
            providers,
          );

          const briefOwner = data.tenant.company_name
            || `${data.tenant.first_name} ${data.tenant.last_name}`;

          const image = _get(data, 'image.original.url')
            ? {
              name: _get(data, 'image.original.file_name'),
              url: _get(data, 'image.original.url'),
            }
            : null;

          let uploadStatus = data.has_file ? 2 : 0;
          // This needs to be last as it overwrites everything
          if (isUploading || isDeletingUpload) uploadStatus = 1;

          const formDisabled = !documentRights.canWrite
            || !!relationsNotification
            || isUploading
            || isDeletingUpload;
          const writableForAgentDisabled = !!relationsNotification
            || isUploading
            || isDeletingUpload;
          const uploadDisabled = !documentRights.canWrite
            || !!relationsNotification
            || isModifying;

          let initTab = 'general_information';
          if (documentRights.isOwner) initTab = 'tracks';

          return (
            <Fragment>
              <LeftColumn>
                <GenericCover
                  placeholder={playlistplaceholder}
                  cover={getGenericDocCoverUrl(data.image)}
                />
                <DocumentName>
                  {data.title}
                </DocumentName>
                <DocumentType>
                  {t(`common:entities.${entity}`)}
                </DocumentType>
                <Autotag.Status
                  autotaggingData={_get(additionalData, 'autotagging.data')}
                  documentRights={documentRights}
                  entity={entity}
                  t={t}
                />
                <DocumentCreatedAt>
                  {t(`pages:meta.${entity}s.panel.created_at`)}
                  :&nbsp;
                  {formatDateString(data.created_at, localPreferences.dateFormat, true)}
                </DocumentCreatedAt>
                <CopyLink
                  link={shareLink}
                  content={t(`pages:meta.${entity}s.panel.copy_share_link`)}
                  tooltipContent={t(`pages:meta.${entity}s.panel.copy_share_link_tooltip`)}
                />
                {!!_get(additionalData, 'tracks.data.length') && (
                  <GenericOwnership>
                    <GenericOwnershipCount>
                      {t('common:entities.number_tracks', {
                        count: _get(additionalData, 'tracks.data.length'),
                      })}
                    </GenericOwnershipCount>
                  </GenericOwnership>
                )}

                <Autotag.ColumnAction
                  autotagDocument={autotagDocument}
                  autotaggingData={_get(additionalData, 'autotagging.data')}
                  documentRights={documentRights}
                  entity={entity}
                  t={t}
                />

                <MetaDownloadDropdown
                  entity={entity}
                  documentId={data.id}
                  documentName={data.title}
                  canDownload={!!_get(additionalData, 'tracks.data.length')}
                  togglerElement={(
                    <DownloadActionWrapper>
                      <DownloadActionIcon />
                      {t(`pages:meta.${entity}s.downloads.toggler_title`)}
                    </DownloadActionWrapper>
                  )}
                />

                {!!_get(additionalData, 'tracks.data.length') && (
                  <WithMetaSearchRoute
                    entity="track"
                    filters={{ internal_refs: [[data.id, `${t(`common:entities.${entity}`)}: ${data.title}`]] }}
                    resetQuery
                  >
                    {routeQuery => (
                      <GenericSearchLink onClick={() => Router.push(`/meta/tracks${routeQuery}`)}>
                        <GenericSearchIcon />
                        {t(`pages:meta.${entity}s.panel.search_tracks`)}
                      </GenericSearchLink>
                    )}
                  </WithMetaSearchRoute>
                )}

                {!!relationsNotification && (
                  <RelationsWrapper>
                    <RelationTitleWrapper>
                      {t(`pages:meta.${entity}s.panel.ongoing_relations_job`)}
                      <RelationJobTooltip
                        content={t(`pages:meta.${entity}s.panel.ongoing_relations_job_tooltip`)}
                      >
                        <Icons.Informations />
                      </RelationJobTooltip>
                    </RelationTitleWrapper>
                    <LineLoaderWrapper>
                      <LineLoader>
                        <LineInnerLoader progress={relationsNotification.progress} />
                      </LineLoader>
                    </LineLoaderWrapper>
                  </RelationsWrapper>
                )}
              </LeftColumn>
              <RightColumn initTab={initTab}>
                <DocumentStatus
                  isModifying={(
                    isModifying
                    || _get(panel, 'isModifying-tracks', false)
                    || _get(panel, 'isModifying-ingest', false)
                  )}
                  isAutotagging={isAutotagging}
                  isDeleting={isDeleting}
                  localPreferences={localPreferences}
                  documentRights={documentRights}
                  updatedAt={data.updated_at}
                  deleteDocument={deleteDocument}
                />
                {this.renderPanelAnchors(data)}
                <DocumentBody>
                  {documentRights.isOwner && (
                    <TabElement name="tracks">
                      <SectionTitle>{t(`pages:meta.${entity}s.panel.tracks_information`)}</SectionTitle>
                      <SectionNotice>
                        {t(`pages:meta.${entity}s.panel.tracks_information_notice`)}
                      </SectionNotice>
                      <DocumentTracks
                        isLoading={_get(panel, 'isLoading-tracks', false)}
                        isModifying={_get(panel, 'isModifying-tracks', false)
                          || _get(panel, 'isModifying-ingest', false)}
                        tenantId={tenantId}
                        entity={entity}
                        parentDocument={data}
                        relationsNotification={relationsNotification}
                        data={_get(additionalData, 'tracks.data', [])}
                        documentRights={documentRights}
                        modifyAdditional={modifyAdditional}
                      />
                    </TabElement>
                  )}

                  <Form
                    onSubmit={() => {}}
                    initialValues={this.pickFormData(data, documentRights, true)}
                    subscription={{ values: true }}
                    keepDirtyOnReinitialize
                    mutators={{ ...arrayMutators }}
                  >
                    {() => (
                      <Fragment>
                        <Autosave
                          debounce={5000}
                          save={modifyDocument}
                          nullValuesTransformer={formValues => this.pickFormData(
                            formValues, documentRights, false,
                          )}
                          autosavePanelId={`${spts.META_BRIEF_PANEL}_${data.id}`}
                        />
                        <TabElement name="general_information">
                          <SectionTitle>
                            {!documentRights.isOwner
                              && t(`pages:meta.${entity}s.panel.general_information`, {
                                briefOwner,
                              })}
                            {documentRights.isOwner
                              && t(`pages:meta.${entity}s.panel.general_information_as_owner`)}
                          </SectionTitle>
                          <SectionNotice>
                            {!documentRights.isOwner
                              && t(`pages:meta.${entity}s.panel.general_information_notice`)}
                            {documentRights.isOwner
                              && t(`pages:meta.${entity}s.panel.general_information_notice_as_owner`)}
                          </SectionNotice>
                          <SectionBox>
                            <InputWrapper>
                              <Field
                                name="title"
                                component={MewoInput}
                                use={Input}
                                type="text"
                                label={t(`pages:meta.${entity}s.panel.title`)}
                                fullWidth
                                validate={validator.title}
                                context="autosave"
                                disabled={formDisabled}
                              />
                              <Field
                                name="deadline"
                                component={MewoInput}
                                use={DatePickerInput}
                                allowNull
                                type="text"
                                label={t(`pages:meta.${entity}s.panel.deadline`)}
                                fullWidth
                                context="autosave"
                                dateFormat={inputDateFormat}
                                withCalendar={documentRights.isOwner}
                                tooltipMessage={documentRights.isOwner
                                  ? t(`pages:meta.${entity}s.panel.deadline_tooltip`)
                                  : undefined}
                                showResetBtn={documentRights.isOwner}
                                disabled={formDisabled || !documentRights.isOwner}
                              />
                              <InputDouble>
                                <Field
                                  name="budget"
                                  component={MewoInput}
                                  use={Input}
                                  type="number"
                                  label={t(`pages:meta.${entity}s.panel.budget`)}
                                  validate={validator.budget}
                                  context="autosave"
                                  disabled={formDisabled || !documentRights.isOwner}
                                />
                                <Field
                                  name="currency"
                                  validate={validator.currency}
                                >
                                  {({ input, meta }) => (
                                    <MewoInput
                                      meta={meta}
                                      input={{
                                        ...input,
                                        onChange: (v) => {
                                          if (v) input.onChange(v);
                                        },
                                      }}
                                      use={SelectInput}
                                      type="text"
                                      options={[
                                        { label: 'Euro', value: 'EUR' },
                                        { label: 'US Dollar', value: 'USD' },
                                        { label: 'British pound sterling', value: 'GBP' },
                                        { label: 'Japanese Yen', value: 'JPY' },
                                      ]}
                                      renderOptionsVariant="classic"
                                      renderValuesVariant="text"
                                      fullWidth
                                      searchable
                                      label={t(`pages:meta.${entity}s.panel.currency`)}
                                      context="autosave"
                                      disabled={formDisabled || !documentRights.isOwner}
                                    />
                                  )}
                                </Field>
                              </InputDouble>
                            </InputWrapper>
                          </SectionBox>
                          <SectionBox>
                            <Field
                              name="descriptions"
                              component={MewoInput}
                              use={InputWithLanguage}
                              defaultLanguage={language}
                              label={t(`pages:meta.${entity}s.panel.descriptions`)}
                              fullWidth
                              isTranslatable
                              inputType="textarea"
                              context="autosave"
                              disabled={formDisabled}
                              height="30rem"
                            />
                          </SectionBox>
                          {documentRights.canAccessNotes && (
                            <SectionBox>
                              <Field
                                name="notes"
                                component={MewoInput}
                                use={TextAreaInput}
                                label={t(`pages:meta.${entity}s.panel.notes`)}
                                fullWidth
                                context="autosave"
                                disabled={formDisabled}
                                height="12rem"
                              />
                            </SectionBox>
                          )}
                        </TabElement>

                        {!documentRights.isOwner && (
                          <TabElement name="tracks">
                            <SectionTitle>{t(`pages:meta.${entity}s.panel.tracks_information`)}</SectionTitle>
                            <SectionNotice>
                              {t(`pages:meta.${entity}s.panel.tracks_information_notice`)}
                            </SectionNotice>
                            <DocumentTracks
                              isLoading={_get(panel, 'isLoading-tracks', false)}
                              isModifying={_get(panel, 'isModifying-tracks', false)}
                              tenantId={tenantId}
                              entity={entity}
                              parentDocument={data}
                              data={_get(additionalData, 'tracks.data', [])}
                              documentRights={documentRights}
                              modifyAdditional={modifyAdditional}
                            />
                          </TabElement>
                        )}

                        {documentRights.isOwner && hasCustomFields && (
                          <TabElement name="custom_fields_information">
                            <SectionTitle>{t(`pages:meta.${entity}s.panel.custom_fields_information`)}</SectionTitle>
                            <SectionNotice>
                              {t(`pages:meta.${entity}s.panel.custom_fields_information_notice`)}
                            </SectionNotice>
                            <SectionBox>
                              <InputWrapper>
                                <MetaCustomFields
                                  entity={entity}
                                  formDisabled={formDisabled}
                                />
                              </InputWrapper>
                            </SectionBox>
                          </TabElement>
                        )}

                        {documentRights.isOwner && (
                          <MetaRightsInformations
                            entity={entity}
                            docAgents={data.agents}
                            docPublic={data.public}
                            documentRights={documentRights}
                            formDisabled={formDisabled}
                            modifyAdditional={modifyAdditional}
                            writableForAgentDisabled={writableForAgentDisabled}
                          />
                        )}
                      </Fragment>
                    )}
                  </Form>

                  {documentRights.isOwner && (
                    <TabElement name="uploads_information">
                      <SectionTitle>{t(`pages:meta.${entity}s.panel.uploads_information`)}</SectionTitle>
                      <SectionNotice>
                        {t(`pages:meta.${entity}s.panel.uploads_information_notice`)}
                      </SectionNotice>
                      <SectionBox>
                        <Uploader
                          name="image"
                          value={image}
                          status={uploadStatus}
                          disabled={uploadDisabled}
                          isUploading={isUploading}
                          uploadProgress={uploadProgress}
                          onChange={(nextValue) => {
                            if (!nextValue) return deleteDocumentFile();
                            return uploadDocumentFile(nextValue);
                          }}
                          maxSize={entityConfig.panelUploads.image.maxSize}
                        />
                        <FileHelper>
                          {t(`pages:meta.${entity}s.panel.image`)}
                        </FileHelper>
                        {_get(data, 'image.error_key') && (
                          <MiscHelper mt="1.2rem" isError>
                            {t(`errors:conversion.${_get(data, 'image.error_key')}_desc`)}
                          </MiscHelper>
                        )}
                      </SectionBox>
                    </TabElement>
                  )}
                </DocumentBody>
              </RightColumn>
            </Fragment>
          );
        }}
      </BaseSidePanel>
    );
  }
}

function mapStateToProps({ core, user, meta }) {
  return {
    relationsNotifications: user.relationsNotifications.briefs,
    tenantId: user.data.tenant_id || user.data.id,
    localPreferences: core.localPreferences,
    customFields: meta.customFields.data,
    providers: meta.providers,
  };
}

export default compose(
  connect(mapStateToProps, {
    triggerPanelAutosaveCheck: triggerAutosaveCheck,
  }),
  withTranslation(['common', 'pages', 'components', 'errors']),
)(MetaBrief);
